import { GateCheckInStatus } from "./constants";

const { ARRIVED, CALLED, DOCKED } = GateCheckInStatus;

/**
 * A yard's post-check-in stops (ReloadYard.driverStops) and message
 * (ReloadYard.postCheckInMessage), read the way every gate surface has to
 * agree on them.
 *
 * The message is what the driver reads the moment they have checked in. The
 * stops are the places the gate sends a called driver through, in order; the
 * last of them is where the crew place the load. The list is optional, and
 * an empty one means something: the yard has no waiting area, so there is no
 * call to wait for. Its check-ins open CALLED (see initialGateStatus) and
 * the message is the driver's only instruction — "proceed to the dock", in
 * the yard's words — while the crew's Set Placed is live at once.
 *
 * With stops, a check-in opens ARRIVED and waits: the manager calls the
 * driver (sending them to the first stop), moves them along, and the crew
 * place the load once the driver reaches the last stop.
 *
 * A yard with no list at all — the field absent, not empty — is one that
 * predates the setting and has never been asked. It waits too: every gate ran
 * a waiting line before stops existed, and reading an unsaved field as "no
 * waiting area" would wave that yard's trucks through, and hand its crew Set
 * Placed at once, the day the field ships. Saving the yard stores the list
 * (empty or not) and settles the question — see yardQueuesDrivers.
 */

export const DEFAULT_POST_CHECK_IN_MESSAGE = "Wait here until called.";

const isBlank = (value) => !value || !String(value).trim();

/**
 * The stops the gate works from, for a yard record: every stored row with a
 * label, in order. An empty array for a yard that stored none, or a broken
 * value.
 */
export const effectiveDriverStops = (reloadYard) =>
  (Array.isArray(reloadYard?.driverStops) ? reloadYard.driverStops : []).filter(
    (stop) => stop && typeof stop.label === "string" && !isBlank(stop.label),
  );

/** Whether the yard has a route at all. Without one there is no waiting. */
export const hasDriverStops = (stops) =>
  Array.isArray(stops) && stops.length > 0;

/**
 * Whether the yard's check-ins wait to be called. Yes with stops listed, no
 * with an explicit empty list (the yard chose no waiting area), and yes again
 * with no list stored at all — see the header: a yard never saved since stops
 * existed keeps the waiting line it always ran. Reads the record, not the
 * effective stops, because effectiveDriverStops collapses "absent" and
 * "empty" into one array and this is the one place they differ.
 */
export const yardQueuesDrivers = (reloadYard) =>
  !Array.isArray(reloadYard?.driverStops) ||
  hasDriverStops(effectiveDriverStops(reloadYard));

/**
 * The status a fresh check-in opens at: ARRIVED where the yard queues its
 * drivers (yardQueuesDrivers), CALLED where it has chosen not to. driverApi
 * (lib/stops.js initialCheckInStatus) applies the same rule to the kiosk's
 * rows, and the two have to agree or the same yard would queue one truck and
 * wave the next straight in.
 */
export const initialGateStatus = (reloadYard) =>
  yardQueuesDrivers(reloadYard) ? ARRIVED : CALLED;

/** What a driver reads after checking in, in the yard's words or the app's. */
export const postCheckInMessage = (reloadYard) =>
  isBlank(reloadYard?.postCheckInMessage)
    ? DEFAULT_POST_CHECK_IN_MESSAGE
    : String(reloadYard.postCheckInMessage).trim();

/** Where a called driver goes first, and where the crew place the load. Null
 * on a yard without stops. */
export const firstStop = (stops) => stops[0] || null;
export const lastStop = (stops) => stops[stops.length - 1] || null;

const sameLabel = (a, b) =>
  !isBlank(a) && !isBlank(b) && String(a).trim() === String(b).trim();

/** Whether a dock assignment names the last stop. Without stops there is no
 * last stop to reach, so the driver is there already: vacuously true, which
 * is what lets the crew place a no-waiting-area truck the moment it checks
 * in. */
export const isAtLastStop = (stops, dockAssignment) =>
  !hasDriverStops(stops) || sameLabel(lastStop(stops)?.label, dockAssignment);

/** The stop a dock assignment names, or null for blank and for a value that
 * is not on the list (typed before the list existed, or a stop since
 * renamed). */
export const stopFor = (stops, dockAssignment) =>
  stops.find((stop) => sameLabel(stop.label, dockAssignment)) || null;

/** The stop after the one a dock assignment names, or the first stop when it
 * names none; null once the driver is at the last stop, and on a yard with
 * no stops. */
export const nextStop = (stops, dockAssignment) => {
  const current = stops.findIndex((stop) =>
    sameLabel(stop.label, dockAssignment),
  );
  if (current < 0) return stops[0] || null;
  return stops[current + 1] || null;
};

/**
 * What the rules make of a status-and-dock edit before it is written, applied
 * identically by the single writer and by the controls that stage the edit:
 *
 *  - moving to CALLED with no dock sends the driver to the first stop — a
 *    call is a place to go, not a bare status;
 *  - assigning a dock to a waiting (ARRIVED) driver IS the call, so the status
 *    follows — the two edits were one decision, and a row left ARRIVED with a
 *    dock would tell the driver to move while the queue still lists them as
 *    waiting;
 *  - moving to DOCKED with no dock names the last stop, where the load is
 *    placed, so the driver app has a destination to show; moving to DOCKED
 *    from an earlier route stop names the last one too — docked is at the
 *    dock, and a row left DOCKED at the scale keeps the scale's message on
 *    the driver's phone while the crew work the load. A dock off the route
 *    (typed, not picked) is kept as given.
 *
 * Each rule fires on the move, never on a row that already stands there: a
 * name correction on a CALLED row with no dock (written before the rule) must
 * not quietly send its driver somewhere, and a waiting row that already
 * carries a dock is not called again by an edit that leaves the dock alone.
 * On a yard with no stops the fills have nothing to fill from and the dock
 * stays as given.
 *
 * `status` and `dockAssignment` are the values as they would be written
 * (edit applied over the row); `current` is the row as saved. Returns the two
 * values to write; nothing else about the edit changes.
 */
export const applyStopRules = ({ stops, current, status, dockAssignment }) => {
  let nextStatus = status;
  let nextDock = dockAssignment;
  const dockGiven = !isBlank(nextDock);
  const dockChanged =
    (nextDock || "") !== (current?.dockAssignment || "");
  if (
    nextStatus === ARRIVED &&
    current?.status === ARRIVED &&
    dockGiven &&
    dockChanged
  )
    nextStatus = CALLED;
  if (nextStatus === CALLED && current?.status !== CALLED && !dockGiven)
    nextDock = firstStop(stops)?.label ?? nextDock;
  if (nextStatus === DOCKED && current?.status !== DOCKED) {
    if (!dockGiven) nextDock = lastStop(stops)?.label ?? nextDock;
    else if (stopFor(stops, nextDock) && !isAtLastStop(stops, nextDock))
      nextDock = lastStop(stops).label;
  }
  return { status: nextStatus, dockAssignment: nextDock };
};

/**
 * One line for a queue row saying where the driver is on the route, or null
 * when there is no driver (no check-in), the visit is over, or the yard has
 * no route to be on.
 */
export const stopHint = (stops, checkIn) => {
  if (!checkIn || !hasDriverStops(stops)) return null;
  const { status, dockAssignment } = checkIn;
  if (status === ARRIVED) return "Waiting to be called";
  if (status !== CALLED && status !== DOCKED) return null;
  const at = stopFor(stops, dockAssignment);
  if (!at) {
    if (isBlank(dockAssignment))
      return status === DOCKED ? "At the dock" : "Called";
    return `At ${String(dockAssignment).trim()}`;
  }
  const next = nextStop(stops, dockAssignment);
  if (status === DOCKED || !next) return `At ${at.label}`;
  return `At ${at.label} — next ${next.label}`;
};
